/**
 * Test Job Purge Page
 * ===================
 * Safe identification and deletion of test/demo jobs.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  RefreshCw, Trash2, AlertTriangle, CheckCircle2, XCircle,
  Search, Calendar, FileText, Eye, Shield, Archive,
  ChevronDown, ChevronUp, Loader2, History
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Flag colors
const FLAG_COLORS = {
  "contains_test": "bg-amber-100 text-amber-700",
  "contains_demo": "bg-amber-100 text-amber-700",
  "contains_dummy": "bg-amber-100 text-amber-700",
  "contains_sample": "bg-amber-100 text-amber-700",
  "test_email": "bg-purple-100 text-purple-700",
  "is_test_flag": "bg-red-100 text-red-700",
  "test_runner_created": "bg-red-100 text-red-700",
  "test_park_name": "bg-blue-100 text-blue-700"
};

// Deletion Log Modal
const DeletionLogModal = ({ open, onClose, logs }) => {
  if (!open) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <History className="h-5 w-5 text-slate-500" />
            Deletion History
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto max-h-[60vh]">
          {logs.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No deletion history</p>
          ) : (
            <div className="space-y-3">
              {logs.map((log, i) => (
                <div key={i} className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={log.mode === "hard_delete" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>
                      {log.mode === "hard_delete" ? "Hard Delete" : "Soft Delete"}
                    </Badge>
                    <span className="text-xs text-slate-500">
                      {new Date(log.deleted_at).toLocaleString("en-GB")}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700">
                    {log.success_count} jobs deleted by {log.deleted_by}
                  </p>
                  {log.deleted_counts && (
                    <div className="text-xs text-slate-500 mt-1">
                      Related: {log.deleted_counts.quotes || 0} quotes, {log.deleted_counts.notes || 0} notes
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function TestJobPurgePage() {
  const navigate = useNavigate();
  
  // State
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState({ count: 0, safe_count: 0, manual_review_count: 0 });
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [search, setSearch] = useState("");
  const [includeAggressive, setIncludeAggressive] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Load preview
  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        include_aggressive: includeAggressive,
        limit: "200"
      });
      if (search) params.append("search", search);
      
      const res = await axios.get(`${API}/api/admin/test-jobs/preview?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.ok) {
        setJobs(res.data.jobs || []);
        setSummary(res.data.summary || {});
      }
    } catch (err) {
      console.error("Failed to load preview:", err);
      toast.error("Failed to load test jobs");
    } finally {
      setLoading(false);
    }
  }, [includeAggressive, search]);
  
  // Load stats
  const loadStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/admin/test-jobs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.ok) {
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  };
  
  // Load deletion logs
  const loadLogs = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/admin/test-jobs/deletion-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.ok) {
        setLogs(res.data.logs || []);
      }
    } catch (err) {
      console.error("Failed to load logs:", err);
    }
  };
  
  useEffect(() => {
    loadPreview();
    loadStats();
  }, [loadPreview]);
  
  // Toggle selection
  const toggleSelection = (jobId) => {
    setSelectedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };
  
  // Select all safe
  const selectAllSafe = () => {
    const safeIds = jobs.filter(j => j.safe_to_delete).map(j => j.job_id);
    setSelectedJobs(new Set(safeIds));
  };
  
  // Clear selection
  const clearSelection = () => {
    setSelectedJobs(new Set());
  };
  
  // Purge jobs
  const purgeJobs = async (mode) => {
    if (selectedJobs.size === 0) {
      toast.error("No jobs selected");
      return;
    }
    
    const jobIds = Array.from(selectedJobs);
    const safeCount = jobIds.filter(id => jobs.find(j => j.job_id === id)?.safe_to_delete).length;
    
    if (mode === "hard_delete" && safeCount < jobIds.length) {
      toast.error("Cannot hard delete jobs that require review");
      return;
    }
    
    const confirmMsg = mode === "hard_delete"
      ? `PERMANENTLY DELETE ${jobIds.length} jobs and all related data? This cannot be undone.`
      : `Archive ${jobIds.length} jobs? They will be hidden from operational views.`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setPurging(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/api/admin/test-jobs/purge`, {
        job_ids: jobIds,
        mode,
        confirmed: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.ok) {
        toast.success(`${mode === "hard_delete" ? "Deleted" : "Archived"} ${res.data.success} jobs`);
        setSelectedJobs(new Set());
        loadPreview();
        loadStats();
      } else {
        toast.error(res.data.error || "Purge failed");
      }
    } catch (err) {
      console.error("Purge failed:", err);
      toast.error("Failed to purge jobs");
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="test-job-purge-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-500" />
                Test Job Purge
              </h1>
              <p className="text-sm text-slate-500">Safely remove test/demo data from the system</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  loadLogs();
                  setShowLogs(true);
                }}
                variant="outline"
                size="sm"
              >
                <History className="h-4 w-4 mr-2" />
                View Log
              </Button>
              <Button
                onClick={() => navigate("/admin/data-cleanup")}
                variant="outline"
                size="sm"
              >
                Data Cleanup
              </Button>
            </div>
          </div>
          
          {/* Warning Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-800">Caution: Irreversible Action</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Only remove test/demo data. Live operational records must not be deleted. 
                  Always preview before deleting. Hard delete cannot be undone.
                </p>
              </div>
            </div>
          </div>
          
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="text-2xl font-bold text-slate-800">{stats.total_jobs}</div>
                <div className="text-xs text-slate-500">Total Jobs</div>
              </div>
              <div className="bg-amber-50 rounded-xl p-3">
                <div className="text-2xl font-bold text-amber-700">{stats.estimated_test_candidates}</div>
                <div className="text-xs text-amber-600">Est. Test Jobs</div>
              </div>
              <div className="bg-green-50 rounded-xl p-3">
                <div className="text-2xl font-bold text-green-700">{summary.safe_count}</div>
                <div className="text-xs text-green-600">Safe to Delete</div>
              </div>
              <div className="bg-red-50 rounded-xl p-3">
                <div className="text-2xl font-bold text-red-700">{stats.deleted_jobs}</div>
                <div className="text-xs text-red-600">Already Deleted</div>
              </div>
            </div>
          )}
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by reference, description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadPreview()}
                className="pl-9"
              />
            </div>
            
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeAggressive}
                onChange={(e) => setIncludeAggressive(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span className="text-slate-600">Include aggressive matches</span>
            </label>
            
            <Button onClick={loadPreview} variant="outline" size="sm" disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-20">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-800">No Test Jobs Found</h3>
            <p className="text-slate-500">The system is clean of obvious test data.</p>
          </div>
        ) : (
          <>
            {/* Selection Actions */}
            <div className="flex items-center justify-between mb-4 p-3 bg-white rounded-xl border border-slate-200">
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">
                  {selectedJobs.size} of {jobs.length} selected
                </span>
                <Button onClick={selectAllSafe} variant="ghost" size="sm">
                  Select All Safe ({summary.safe_count})
                </Button>
                <Button onClick={clearSelection} variant="ghost" size="sm">
                  Clear
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => purgeJobs("soft_delete")}
                  disabled={selectedJobs.size === 0 || purging}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  {purging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Archive className="h-4 w-4 mr-2" />}
                  Archive Selected
                </Button>
                <Button
                  onClick={() => purgeJobs("hard_delete")}
                  disabled={selectedJobs.size === 0 || purging}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {purging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Delete Permanently
                </Button>
              </div>
            </div>
            
            {/* Jobs Grid */}
            <div className="space-y-3">
              {jobs.map(job => {
                const isSelected = selectedJobs.has(job.job_id);
                
                return (
                  <div 
                    key={job.job_id}
                    className={`bg-white rounded-xl border p-4 transition-all ${
                      isSelected 
                        ? "border-teal-400 ring-1 ring-teal-200" 
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <div className="pt-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelection(job.job_id)}
                          className="rounded border-slate-300 w-5 h-5"
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-medium text-slate-800">
                            {job.job_reference}
                          </span>
                          {job.safe_to_delete ? (
                            <Badge className="bg-green-100 text-green-700">
                              <Shield className="h-3 w-3 mr-1" />
                              SAFE
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              REVIEW
                            </Badge>
                          )}
                          <Badge className="bg-slate-100 text-slate-600">
                            {job.status || "Unknown"}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-slate-600 mb-2">
                          {job.park_name} • {job.description}
                        </p>
                        
                        {/* Reason Flags */}
                        <div className="flex flex-wrap gap-1 mb-2">
                          {job.reason_flags?.map((flag, i) => (
                            <span 
                              key={i}
                              className={`text-xs px-2 py-0.5 rounded-full ${FLAG_COLORS[flag] || "bg-slate-100 text-slate-600"}`}
                            >
                              {flag.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                        
                        {/* Linked Counts */}
                        <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                          {job.linked_counts?.quotes > 0 && (
                            <span>📋 {job.linked_counts.quotes} quotes</span>
                          )}
                          {job.linked_counts?.invoices > 0 && (
                            <span className={job.linked_counts.paid_invoices > 0 ? "text-red-600 font-medium" : ""}>
                              💰 {job.linked_counts.invoices} invoices
                              {job.linked_counts.paid_invoices > 0 && ` (${job.linked_counts.paid_invoices} paid)`}
                            </span>
                          )}
                          {job.linked_counts?.notes > 0 && (
                            <span>📝 {job.linked_counts.notes} notes</span>
                          )}
                          {job.linked_counts?.documents > 0 && (
                            <span>📎 {job.linked_counts.documents} docs</span>
                          )}
                          {job.linked_counts?.communications > 0 && (
                            <span>💬 {job.linked_counts.communications} comms</span>
                          )}
                        </div>
                        
                        {/* Blockers */}
                        {job.safety_blockers?.length > 0 && (
                          <div className="mt-2 text-xs text-red-600">
                            <strong>Blockers:</strong> {job.safety_blockers.join(", ")}
                          </div>
                        )}
                      </div>
                      
                      {/* Date */}
                      <div className="text-xs text-slate-400 text-right flex-shrink-0">
                        {job.created_at ? new Date(job.created_at).toLocaleDateString("en-GB") : "-"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      
      {/* Deletion Log Modal */}
      <DeletionLogModal open={showLogs} onClose={() => setShowLogs(false)} logs={logs} />
    </div>
  );
}
